import React, { useState, useEffect } from 'react';
import { useAdmin } from './AdminContext';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { ClipboardList, User, Building2, Truck, Database, LogIn } from 'lucide-react';

const ENTITY_ICONS = { user: User, client: Building2, vendor: Truck, system: Database };
const ACTION_COLORS = { created: '#10B981', updated: '#3B82F6', deactivated: '#F59E0B', uploaded: '#8B5CF6', login: '#6B7280', seeded: '#0EA5E9', cleared: '#EF4444' };

export default function AdminActivityLog() {
  const { authFetch } = useAdmin();
  const [logs, setLogs] = useState([]);
  const [entityFilter, setEntityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');

  const load = async () => {
    try {
      const q = new URLSearchParams();
      if (entityFilter !== 'all') q.set('entity_type', entityFilter);
      if (actionFilter !== 'all') q.set('action', actionFilter);
      q.set('limit', '200');
      const res = await authFetch(`/api/admin/activity-log?${q}`);
      if (res.ok) setLogs(await res.json());
    } catch {}
  };

  useEffect(() => { load(); }, [entityFilter, actionFilter]);

  const formatTime = (ts) => {
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
  };

  return (
    <div data-testid="admin-activity-log">
      <h1 className="text-2xl font-bold mb-6" style={{ color: '#1B2A6B', fontFamily: 'Chivo, sans-serif' }}>Activity Log</h1>

      <Card className="border border-gray-200 mb-6"><CardContent className="p-4">
        <div className="flex gap-3 flex-wrap">
          <Select value={entityFilter} onValueChange={setEntityFilter}>
            <SelectTrigger className="w-36" data-testid="entity-filter"><SelectValue placeholder="Entity" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Entities</SelectItem>
              <SelectItem value="user">Users</SelectItem>
              <SelectItem value="client">Clients</SelectItem>
              <SelectItem value="vendor">Vendors</SelectItem>
              <SelectItem value="system">System</SelectItem>
            </SelectContent>
          </Select>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-36" data-testid="action-filter"><SelectValue placeholder="Action" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="created">Created</SelectItem>
              <SelectItem value="updated">Updated</SelectItem>
              <SelectItem value="deactivated">Deactivated</SelectItem>
              <SelectItem value="uploaded">Uploaded</SelectItem>
              <SelectItem value="login">Login</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent></Card>

      <div className="space-y-2">
        {logs.length === 0 && <p className="text-center text-gray-400 py-12">No activity found</p>}
        {logs.map(l => {
          const Icon = ENTITY_ICONS[l.entity_type] || ClipboardList;
          return (
            <Card key={l.id} className="border border-gray-100" data-testid={`log-${l.id}`}>
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${ACTION_COLORS[l.action] || '#6B7280'}15` }}>
                    <Icon className="w-4 h-4" style={{ color: ACTION_COLORS[l.action] || '#6B7280' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium" style={{ color: '#1B2A6B' }}>{l.user_name}</span>
                      <Badge className="text-xs capitalize" style={{ background: `${ACTION_COLORS[l.action] || '#6B7280'}15`, color: ACTION_COLORS[l.action] || '#6B7280' }}>{l.action}</Badge>
                      <span className="text-sm text-gray-500">{l.entity_type}: <strong>{l.entity_name}</strong></span>
                    </div>
                    {l.details && <p className="text-xs text-gray-400 mt-0.5">{l.details}</p>}
                    <p className="text-xs text-gray-300 mt-0.5">{formatTime(l.timestamp)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
